// src/contexts/ThemeContext.js
import React, { createContext, useState, useEffect, useContext } from "react";

// Define theme constants
export const themes = {
  dark: "dark",
  light: "light",
};

// Map theme to CSS class (you can adjust this)
export const themeClassMap = {
  [themes.dark]: "", // no class → dark mode
  [themes.light]: "white-content", // adds light mode class
};

// Create context with default values
export const ThemeContext = createContext({
  theme: themes.dark,
  toggleTheme: () => {},
  setTheme: () => {},
});

// Custom hook for easy consumption
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Provider component
export const ThemeProvider = ({ children }) => {
  // Initialize from localStorage or system preference
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem("app-theme");
    if (savedTheme && Object.values(themes).includes(savedTheme)) {
      return savedTheme;
    }

    // Fallback to system preference
    const systemPrefersDark = window.matchMedia?.(
      "(prefers-color-scheme: dark)"
    ).matches;
    return systemPrefersDark ? themes.dark : themes.light;
  };

  const [theme, setThemeState] = useState(getInitialTheme);

  // Update DOM class and localStorage when theme changes
  useEffect(() => {
    // Update <body> class
    const classToAdd = themeClassMap[theme];
    const classToRemove =
      theme === themes.light
        ? themeClassMap[themes.dark]
        : themeClassMap[themes.light];

    // Clean up previous class if needed
    Object.values(themeClassMap).forEach((cls) => {
      if (cls && document.body.classList.contains(cls)) {
        document.body.classList.remove(cls);
      }
    });

    if (classToAdd) {
      document.body.classList.add(classToAdd);
    }

    // Persist in localStorage
    localStorage.setItem("app-theme", theme);
  }, [theme]);

  // Optional: Listen for system theme changes (if no user override)
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e) => {
      const savedTheme = localStorage.getItem("app-theme");
      // Only follow system preference if user hasn't manually chosen a theme
      if (!savedTheme) {
        setThemeState(e.matches ? themes.dark : themes.light);
      }
    };

    // Set initial theme based on system (if no saved preference)
    const savedTheme = localStorage.getItem("app-theme");
    if (!savedTheme) {
      setThemeState(mediaQuery.matches ? themes.dark : themes.light);
    }

    // Listen for future changes
    mediaQuery.addEventListener("change", handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  const toggleTheme = () => {
    setThemeState((prev) =>
      prev === themes.dark ? themes.light : themes.dark
    );
  };

  const setTheme = (newTheme) => {
    if (Object.values(themes).includes(newTheme)) {
      setThemeState(newTheme);
    } else {
      console.warn("Invalid theme:", newTheme);
    }
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        setTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
